import { useOfficialStore } from '$/store/useOfficialStore';
import { mtstatTrack } from './mtstat';

export const GUIDE_PAY_VIP_PROMPT_SOURCE = 'carrot_insufficient_subscribe' as const;

export type VipPromptTrackSource =
  | typeof GUIDE_PAY_VIP_PROMPT_SOURCE
  | 'my_carrot_upgrade'
  | 'asset_account_entry'
  | 'asset_library_entry'
  | 'asset_left_upgrade'
  | 'concurrency_limit_node'
  | 'asset_node_upgrade'
  | 'brand_watermark_workflow'
  | 'brand_watermark_preview'
  | 'data_dashboard'
  | 'team_asset_space'
  | 'team_admin_page'
  | 'team_add_user_pop';

type VipPromptMemberStatus = 'non_member' | 'vip' | 'fetch_failed';

interface VipPromptTrackParams {
  oid: string;
  is_admin: string;
  room_id: string;
  task_id: string;
  sub_task_id: string;
  from_source: VipPromptTrackSource;
  member_status: VipPromptMemberStatus;
  level: string;
  invalid_time: string;
}

export type VipPromptTrackContext = Partial<Pick<
VipPromptTrackParams,
'oid' | 'is_admin' | 'room_id' | 'task_id' | 'sub_task_id'
>>;

/** 读取当前页面房间 ID；非浏览器环境或 URL 未携带时按埋点文档上报空值。 */
function getCurrentRoomId(): string {
  if (typeof window === 'undefined') return '';
  return new URLSearchParams(window.location.search).get('room_id') || '';
}

/** 读取当前团队身份；上下文显式传入时由业务入口覆盖。 */
function getCurrentTeamTrackParams(): Pick<VipPromptTrackParams, 'oid' | 'is_admin'> {
  const { clientOId, clientOIsAdmin } = useOfficialStore.getState();
  let isAdmin = '';
  if (clientOIsAdmin != null) {
    isAdmin = clientOIsAdmin ? '1' : '0';
  }
  return {
    oid: clientOId == null ? '' : String(clientOId),
    is_admin: isAdmin,
  };
}

/** 根据会员信息快照和业务任务上下文组装购买提示埋点参数。 */
function buildVipPromptTrackParams(
  fromSource: VipPromptTrackSource,
  context: VipPromptTrackContext = {},
): VipPromptTrackParams {
  const { subscribe } = useOfficialStore.getState().userInfo;
  const currentTeamParams = getCurrentTeamTrackParams();
  let memberStatus: VipPromptMemberStatus = 'fetch_failed';
  if (subscribe) {
    memberStatus = subscribe.isValid ? 'vip' : 'non_member';
  }
  const isVip = memberStatus === 'vip';

  return {
    oid: context.oid ?? currentTeamParams.oid,
    is_admin: context.is_admin ?? currentTeamParams.is_admin,
    room_id: context.room_id ?? getCurrentRoomId(),
    task_id: context.task_id ?? '',
    sub_task_id: context.sub_task_id ?? '',
    from_source: fromSource,
    member_status: memberStatus,
    level: isVip ? String(subscribe?.level ?? '') : '',
    invalid_time: isVip ? String(subscribe?.invalidTime ?? '') : '',
  };
}

/** 上报购买会员或充值萝卜提示信息曝光。 */
export function trackRoboneoVipShow(
  fromSource: VipPromptTrackSource,
  context?: VipPromptTrackContext,
): void {
  mtstatTrack('roboneo_vip_show', buildVipPromptTrackParams(fromSource, context));
}

/** 上报购买会员或充值萝卜提示信息点击。 */
export function trackRoboneoVipClick(
  fromSource: VipPromptTrackSource,
  context?: VipPromptTrackContext,
): void {
  mtstatTrack('roboneo_vip_click', buildVipPromptTrackParams(fromSource, context));
}
